import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'

import { Spin } from 'antd'

import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import {
  CameraCapture,
  type CameraCaptureHandle,
} from 'features/Procedure/features/notes/components/procedure-image-list/camera-capture'
import { useUploadPatientProfileImageMutation } from 'features/Procedure/state/procedure.api'

import styles from './intake-selfie-step.module.scss'

type IntakeSelfieStepProps = {
  siteId: string
  patientId: number
  onRemove?: () => void
}

export type IntakeSelfieStepHandle = {
  commit: () => Promise<boolean>
}

export const IntakeSelfieStep = forwardRef<IntakeSelfieStepHandle, IntakeSelfieStepProps>(
  ({ siteId, patientId, onRemove }, ref) => {
    const { setNotification } = useNotification()
    const inFlightRef = useRef(false)
    const cameraRef = useRef<CameraCaptureHandle>(null)
    const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null)
    const [cameraReady, setCameraReady] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [uploadProfileImage] = useUploadPatientProfileImageMutation()

    const commit = useCallback(async (): Promise<boolean> => {
      if (!capturedDataUrl) return true
      if (inFlightRef.current) return true
      inFlightRef.current = true
      setIsSaving(true)
      try {
        const blob = await fetch(capturedDataUrl).then((response) => response.blob())
        const fileName = `profile-${patientId}.jpg`
        const file = new File([blob], fileName, { type: 'image/jpeg' })
        const formData = new FormData()
        formData.append('photo', file, fileName)
        formData.append('file_name', fileName)

        await uploadProfileImage({ siteId, patientId, formData }).unwrap()
        setNotification({
          type: ALERT_CONSTANTS.SUCCESS,
          duration: 2,
          title: 'Saved',
          description: 'Profile photo uploaded',
        })
        setCapturedDataUrl(null)
        setIsSaving(false)
        return true
      } catch (error: unknown) {
        const description = error instanceof Error ? error.message : 'Failed to upload the photo'
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description,
        })
        setIsSaving(false)
        return false
      } finally {
        inFlightRef.current = false
      }
    }, [capturedDataUrl, siteId, patientId, uploadProfileImage, setNotification])

    useImperativeHandle(ref, () => ({ commit }), [commit])

    const handleRetake = useCallback(() => {
      setCapturedDataUrl(null)
    }, [])

    return (
      <div className={styles.wrapper}>
        {capturedDataUrl ? (
          <div className={styles.preview}>
            <img className={styles.previewImage} src={capturedDataUrl} alt='Captured profile' />
            {isSaving ? (
              <div className={styles.uploading}>
                <Spin size='large' />
                <p className={styles.hint}>Uploading photo…</p>
              </div>
            ) : (
              <div className={styles.previewActions}>
                <Button
                  modifier={BUTTON_MODIFIER.SECONDARY}
                  proportion={BUTTON_PROPORTION.MEDIUM}
                  onClick={handleRetake}>
                  Retake
                </Button>
                {onRemove && (
                  <Button
                    modifier={BUTTON_MODIFIER.SECONDARY}
                    severity={BUTTON_SEVERITY.DANGER}
                    proportion={BUTTON_PROPORTION.MEDIUM}
                    onClick={onRemove}>
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.camera}>
            <CameraCapture
              ref={cameraRef}
              active
              mirrored
              hideActions
              facingMode='user'
              onReadyChange={setCameraReady}
              onCapture={setCapturedDataUrl}
            />
            <Button
              proportion={BUTTON_PROPORTION.MEDIUM}
              onClick={() => cameraRef.current?.capture()}
              disabled={!cameraReady}>
              Take a selfie
            </Button>
          </div>
        )}
      </div>
    )
  },
)

IntakeSelfieStep.displayName = 'IntakeSelfieStep'
